import React, { useRef } from 'react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { CustomDatePicker } from '../../ui/custom-date-picker';
import { Lock, Paperclip, X, FileText } from 'lucide-react';
import { getTodayString } from '../../../utils/dateUtils';

const DEFAULT_VALUES = {
  delivery_status: 'in_progress',
  acceptance_status: 'pending',
  bill_status: 'pending'
};

const hasSelectedValue = (field, value) => {
  if (!value) return false;
  const defaultVal = DEFAULT_VALUES[field];
  if (defaultVal !== undefined) {
    return String(value) !== String(defaultVal);
  }
  return true;
};

export function StatusContent({ formData, handleFieldChange, compact = false, locked = false, deliveryAttachments = [], setDeliveryAttachments, existingDeliveryFiles = [], onRemoveExistingFile }) {
  const fileInputRef = useRef(null);
  const inputH = 'h-10 rounded-xl';
  const lockedStyle = 'bg-slate-100 cursor-not-allowed opacity-70';

  const handleFileAdd = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0 && setDeliveryAttachments) {
      setDeliveryAttachments(prev => [...prev, ...files]);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileRemove = (index) => {
    if (setDeliveryAttachments) {
      setDeliveryAttachments(prev => prev.filter((_, i) => i !== index));
    }
  };
  
  if (locked) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-slate-500 text-sm mb-2">
          <Lock className="w-4 h-4" />
          <span>This section is locked</span>
        </div>
        <div className={`grid ${compact ? 'grid-cols-1 gap-2' : 'grid-cols-3 gap-3'}`}>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500 ml-1">Delivery Status</Label>
            <Input value={formData.delivery_status === 'delivered' ? 'Delivered' : 'In Progress'} readOnly className={`${inputH} ${lockedStyle}`} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500 ml-1">Date Received</Label>
            <Input value={formData.date_received || ''} readOnly className={`${inputH} ${lockedStyle}`} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500 ml-1">Acceptance Status</Label>
            <Input value={formData.acceptance_status === 'accepted' ? 'Accepted' : formData.acceptance_status === 'rejected' ? 'Rejected' : 'Pending'} readOnly className={`${inputH} ${lockedStyle}`} />
          </div>
        </div>
        {formData.acceptance_status === 'rejected' && formData.rejection_reason && (
          <Input value={formData.rejection_reason} readOnly placeholder="Rejection Reason" className={`${inputH} ${lockedStyle}`} />
        )}
        {/* Show existing attachments in locked mode */}
        {existingDeliveryFiles && existingDeliveryFiles.length > 0 && (
          <div className="space-y-1">
            <Label className="text-xs text-slate-500 ml-1">Attachments</Label>
            <div className="flex flex-wrap gap-2">
              {existingDeliveryFiles.map((file, i) => (
                <div key={i} className="flex items-center gap-1.5 bg-slate-100 px-2.5 py-1.5 rounded-lg">
                  <FileText className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-xs text-slate-600 max-w-[150px] truncate">{file.filename || `File ${i + 1}`}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
  
  return (
    <div className="space-y-3">
      <div className={`grid ${compact ? 'grid-cols-1 gap-2' : 'grid-cols-3 gap-3'}`}>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500 ml-1">Delivery Status</Label>
          <Select value={formData.delivery_status} onValueChange={(v) => handleFieldChange('delivery_status', v)}>
            <SelectTrigger data-testid="delivery-status-select" className={`${inputH} ${hasSelectedValue('delivery_status', formData.delivery_status) ? 'border-slate-900' : ''}`}><SelectValue placeholder="Select Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500 ml-1">Date Received {formData.delivery_status === 'delivered' ? '*' : ''}</Label>
          <CustomDatePicker data-testid="date-received" value={formData.date_received} onChange={(date) => handleFieldChange('date_received', date || '')} placeholder="Select date" min={formData.date_sent || undefined} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500 ml-1">Acceptance Status {formData.delivery_status === 'delivered' ? '*' : ''}</Label>
          <Select value={formData.acceptance_status} onValueChange={(v) => handleFieldChange('acceptance_status', v)}>
            <SelectTrigger data-testid="acceptance-status-select" className={`${inputH} ${hasSelectedValue('acceptance_status', formData.acceptance_status) ? 'border-slate-900' : ''}`}><SelectValue placeholder="Select Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="accepted">Accepted</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {formData.acceptance_status === 'rejected' && (
        <Textarea value={formData.rejection_reason} onChange={(e) => handleFieldChange('rejection_reason', e.target.value)} placeholder="Rejection Reason *" className={`rounded-xl min-h-[60px] ${formData.rejection_reason ? 'border-slate-900' : ''}`} />
      )}
      {formData.delivery_status === 'delivered' && formData.date_received && formData.acceptance_status !== 'accepted' && formData.acceptance_status !== 'rejected' && (
        <div className="p-2 bg-amber-50 border border-amber-200 rounded-xl">
          <p className="text-xs text-amber-700"><strong>Acceptance must be "Accepted"</strong> to proceed</p>
        </div>
      )}

      {/* Delivery Attachments — Optional */}
      <div className="space-y-1.5">
        <Label className="text-xs text-slate-500 ml-1">Attachments (Optional)</Label>
        
        {/* Existing files from DB */}
        {existingDeliveryFiles && existingDeliveryFiles.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-1.5">
            {existingDeliveryFiles.map((file, i) => (
              <div key={`existing-${i}`} className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 px-2.5 py-1.5 rounded-lg" data-testid={`delivery-existing-file-${i}`}>
                <FileText className="w-3.5 h-3.5 text-blue-500" />
                <span className="text-xs text-blue-700 max-w-[150px] truncate">{file.filename || `File ${i + 1}`}</span>
                {onRemoveExistingFile && (
                  <button type="button" onClick={() => onRemoveExistingFile(i)} className="ml-0.5 text-blue-400 hover:text-red-500 transition-colors" data-testid={`remove-existing-delivery-file-${i}`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* New files to upload */}
        {deliveryAttachments && deliveryAttachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-1.5">
            {deliveryAttachments.map((file, i) => (
              <div key={`new-${i}`} className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 px-2.5 py-1.5 rounded-lg" data-testid={`delivery-new-file-${i}`}>
                <FileText className="w-3.5 h-3.5 text-emerald-500" />
                <span className="text-xs text-emerald-700 max-w-[150px] truncate">{file.name}</span>
                <button type="button" onClick={() => handleFileRemove(i)} className="ml-0.5 text-emerald-400 hover:text-red-500 transition-colors" data-testid={`remove-new-delivery-file-${i}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add file button */}
        <input ref={fileInputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="hidden" onChange={handleFileAdd} data-testid="delivery-file-input" />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-500 border border-dashed border-slate-300 rounded-lg hover:border-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-colors"
          data-testid="add-delivery-attachment-btn"
        >
          <Paperclip className="w-3.5 h-3.5" />
          Add Attachment
        </button>
      </div>
    </div>
  );
}
